/**
 * The Issues column of the quantity grid: where an added instrument shows its check, and where the
 * user fixes what the check found. The issue UX is the review page's — its cell renderer and its
 * two dialogs, reused unchanged through {@link toReviewInstrument} — with the fix written to the
 * builder instead of to a review row.
 */
import { spawnInstrumentClassificationDialog } from "$root/functional-areas/instruments/instrument-classification/InstrumentClassificationDialog";
import type { ReviewInstrument } from "$root/functional-areas/proxies/const";
import {
	instrumentIssueColdDef,
	spawnUnrecognizedInstrumentDialog,
} from "$root/ui-lib/table/custom-column/instrument/issue-cell";
import type { IndexTicker } from "@eu.mdotm.api/sphere";
import { ActionText, CircularProgressBar, Icon, Row, Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import type { ColDef } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { useTranslation } from "react-i18next";
import type { Instrument, NewInstrumentCheck } from "../../const";
import type { QuantityEditorBuilderProps } from "../builder";
import { proxiesFromRiskModel } from "./checks";

/** An added row as the review page's issue cell reads one. */
export function toReviewInstrument(row: Instrument): ReviewInstrument {
	const check = row.newInstrumentCheck;
	return {
		...row,
		name: row.name ?? row.alias ?? row.id,
		ticker: row.tickerName,
		deleted: Boolean(row.deleted),
		delisted: check?.status === "unfixable",
		needsCustomProxy: check?.needsCustomProxy,
		proxies: proxiesFromRiskModel(row),
		issue: check?.issue,
		hasBeenReviewed: check?.issueResolved,
	};
}

const ReviewIssueCell = instrumentIssueColdDef().cellRenderer;

export function NewInstrumentIssueCell(props: CustomCellRendererProps<Instrument>): React.JSX.Element | null {
	const { t } = useTranslation();
	const row = props.data;
	const status = row?.newInstrumentCheck?.status;

	if (!row || status == null) {
		return null;
	}

	if (status === "checking") {
		return (
			<Row
				className="h-full w-full"
				alignItems="center"
				gap={4}
				data-qualifier="CompositionEditor/NewInstrumentChecking"
			>
				<CircularProgressBar className="w-3" value="indeterminate" />
				<Text type="Body/M/Medium">{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.CHECKING")}</Text>
			</Row>
		);
	}

	if (status === "failed") {
		return (
			<Row className="h-full w-full" alignItems="center">
				<ActionText
					className="flex items-center gap-1 overflow-x-hidden"
					onClick={noop}
					as="div"
					data-qualifier="CompositionEditor/NewInstrumentCheckFailed"
				>
					<Icon size={18} icon="Alert" color={themeCSSVars.palette_W500} />
					<span className="truncate">{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.CHECK_FAILED")}</span>
				</ActionText>
			</Row>
		);
	}

	return (
		<ReviewIssueCell
			{...(props as unknown as CustomCellRendererProps<ReviewInstrument>)}
			data={toReviewInstrument(row)}
		/>
	);
}

/** The dialog that takes a risk model for the row, fixing it once submitted. */
function askRiskModel(row: Instrument, builder: QuantityEditorBuilderProps): void {
	spawnInstrumentClassificationDialog({
		defaultInstrumentProxies: proxiesFromRiskModel(row),
		onSubmitAsync({ indices, weights }) {
			const proxies = indices.map(
				(index): IndexTicker => ({
					...index,
					weight: index.ticker ? weights.get(index.ticker)?.toNumber() : undefined,
				}),
			);
			builder.fixNewInstrument(row.id, { kind: "riskModel", proxies });
		},
	}).catch(noop);
}

/**
 * Opens the fix the row's issue asks for, the way the review page does: an unrecognised instrument
 * offers a new identifier or a risk model, every other fixable issue asks for a risk model. A check
 * that did not answer is retried. An instrument that cannot be held offers nothing.
 */
function onIssueClicked(row: Instrument | undefined, builder: QuantityEditorBuilderProps): void {
	const check = row?.newInstrumentCheck;
	if (!row || !check || row.deleted) {
		return;
	}
	if (check.status === "failed") {
		builder.fixNewInstrument(row.id, { kind: "retry" });
		return;
	}
	if (check.status !== "fixable") {
		return;
	}
	if (check.issue === "UNRECOGNIZED_INSTRUMENT") {
		spawnUnrecognizedInstrumentDialog({
			instrument: row.alias ?? "",
			onSubmit(fields) {
				if (fields.strategy === "NEW_IDENTIFIER") {
					builder.fixNewInstrument(row.id, { kind: "identifier", identifier: fields.identifier });
					return;
				}
				askRiskModel(row, builder);
			},
		});
		return;
	}
	askRiskModel(row, builder);
}

export const NEW_INSTRUMENT_ISSUE_COL_ID = "NEW_INSTRUMENT_ISSUE";

/**
 * How urgently the row's check asks for the user, the way the review page orders its rows: an issue
 * the user can act on (or a check to retry) first, then a check still out, then an instrument that
 * cannot be held, then every row with nothing to show.
 */
export const ISSUE_RANK: Record<NewInstrumentCheck["status"], number> = {
	fixable: 3,
	failed: 3,
	checking: 2,
	unfixable: 1,
	clear: 0,
};

export function issueRank(row: Instrument | undefined): number {
	const status = row?.newInstrumentCheck?.status;
	return status == null ? 0 : ISSUE_RANK[status];
}

/**
 * The Issues column, for the quantity grid alone. Always pinned left, after the columns already
 * pinned there: a saved layout that unpinned or moved it is overridden on load by `./pinnedIssueColumn`.
 */
export function newInstrumentIssueColumn(builder: QuantityEditorBuilderProps): ColDef<Instrument> {
	return {
		colId: NEW_INSTRUMENT_ISSUE_COL_ID,
		headerName: "Issues",
		initialWidth: 150,
		pinned: "left",
		editable: false,
		// The cell has no value of its own: rows sort by their check, most urgent first on the first click.
		comparator: (_a, _b, nodeA, nodeB) => issueRank(nodeA.data) - issueRank(nodeB.data),
		sortingOrder: ["desc", "asc", null],
		cellRenderer: NewInstrumentIssueCell,
		onCellClicked: (params) => onIssueClicked(params.data, builder),
	};
}
